import { CSSProperties, memo, useMemo } from 'react';
import classNames from 'classnames';
import chroma from 'chroma-js';

import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { ContainerProps } from './container.types';
import { hex, opacity } from '@/types/theme-packs.types';

import styles from './container.module.css';

const colorCache = new Map<string, string | undefined>();

const getColorKey = (color?: string | [hex, opacity]): string => {
  if (Array.isArray(color)) {
    return `${color[0]},${color[1]}`;
  }
  return color || '';
};

const getValidatedColor = (color?: string | [hex, opacity]): string | undefined => {
  if (Array.isArray(color)) {
    const colorKey = getColorKey(color);
    const cached = colorCache.get(colorKey);
    if (cached) {
      return cached;
    }

    const [hex, opacity] = color;
    if (!chroma.valid(hex)) {
      colorCache.set(colorKey, '');
      return '';
    }
    // ensure opacity is a number
    let _opacity = Number(opacity);
    if (isNaN(_opacity)) {
      _opacity = 1;
    }
    // convert to rgba
    const rgba = chroma(hex).alpha(_opacity).css();
    colorCache.set(colorKey, rgba);
    return rgba;
  }

  return color;
};

const Container = memo(
  ({
    children,
    className,
    background: passedBackground,
    backgroundLightMode,
    hoverBackground,
    height,
    width,
    maxWidth,
    minWidth,
    maxHeight,
    minHeight,
    tagName,
    borderRadius,
    borderColor,
    borderWidth,
    borderStyle = 'solid',
    border,
    boxShadow,
    margin,
    padding,
    opacity,
    gap,
    style,
    ref,
    loading,
    loadinBackground,
    skeletonLoader,
    skeletonLoaderBackground,
    onClick,
    gradientOverlay,
    id,
    outline,
    backgroundImage,
    gradientBorder,
    flex,
    aspectRatio,
    transitionDuration = '0.2s',
    inheritButtonHover,
    design,
    ...rest
  }: ContainerProps) => {
    const Tag = tagName || 'div';

    const background = useMemo(() => getValidatedColor(passedBackground), [passedBackground]);
    const lightModeBackground = useMemo(() => getValidatedColor(backgroundLightMode), [backgroundLightMode]);

    return (
      <Tag
        id={id}
        data-id={id}
        className={classNames(
          styles.container,
          {
            [styles.skeletonLoader]: !!skeletonLoader,
            [styles.gradientOverlay]: !!gradientOverlay,
            [styles.gradientBorder]: !!gradientBorder,
            [styles.aspectRatio]: !!aspectRatio,
            [styles.lightModeBackground]: !!lightModeBackground,
            [styles.inheritButtonHover]: inheritButtonHover,
            [styles.border]: !!border,
            ...(design ? { [design]: design } : {}),
          },
          className || ''
        )}
        ref={ref}
        style={
          {
            ...(background && { '--container-component-bg-color': background }),
            ...(lightModeBackground && { '--container-component-bg-light-mode-color': lightModeBackground }),
            ...(hoverBackground && { '--container-component-hover-bg-color': hoverBackground }),
            ...(loadinBackground && { '--container-component-loading-bg-color': loadinBackground }),
            ...(skeletonLoaderBackground && {
              '--container-component-skeleton-bg-start': skeletonLoaderBackground.start,
            }),
            ...(skeletonLoaderBackground && { '--container-component-skeleton-bg-end': skeletonLoaderBackground.end }),
            ...(height && { '--container-component-height': height }),
            ...(width && { '--container-component-width': width }),
            ...(minWidth && { '--container-component-min-width': minWidth }),
            ...(maxWidth && { '--container-component-max-width': maxWidth }),
            ...(maxHeight && { '--container-component-max-height': maxHeight }),
            ...(minHeight && { '--container-component-min-height': minHeight }),
            ...(borderRadius && { '--container-component-border-radius': borderRadius }),
            ...(borderColor && { '--container-component-border-color': borderColor }),
            ...(borderWidth && { '--container-component-border-width': borderWidth }),
            ...(borderStyle && { '--container-component-border-style': borderStyle }),
            ...(border && { '--container-component-border': border }),
            ...(margin && { '--container-component-margin': margin }),
            ...(padding && { '--container-component-padding': padding }),
            ...(gap && { '--container-component-gap': gap }),
            ...(gradientOverlay &&
              'height' in gradientOverlay && { '--gradient-overlay-height': gradientOverlay.height }),
            ...(gradientOverlay && 'width' in gradientOverlay && { '--gradient-overlay-width': gradientOverlay.width }),
            ...(gradientOverlay?.background && { '--gradient-overlay-background': gradientOverlay.background }),
            ...(gradientOverlay &&
              'borderRadius' in gradientOverlay && {
                '--gradient-overlay-border-radius': gradientOverlay.borderRadius,
              }),
            ...(gradientOverlay &&
              'top' in gradientOverlay && {
                '--gradient-overlay-top': gradientOverlay.top,
              }),
            ...(gradientOverlay &&
              'right' in gradientOverlay && {
                '--gradient-overlay-right': gradientOverlay.right,
              }),
            ...(gradientOverlay &&
              'bottom' in gradientOverlay && {
                '--gradient-overlay-bottom': gradientOverlay.bottom,
              }),
            ...(gradientOverlay &&
              'left' in gradientOverlay && {
                '--gradient-overlay-left': gradientOverlay.left,
              }),
            ...(outline && { '--container-component-outline': outline }),
            ...(backgroundImage && { '--container-component-background-image': `url('${backgroundImage}')` }),
            ...(gradientBorder && { '--container-component-gradient-border-from': gradientBorder.from }),
            ...(gradientBorder && { '--container-component-gradient-border-to': gradientBorder.to }),
            ...(gradientBorder && { '--container-component-gradient-border-width': gradientBorder.width || '1px' }),
            ...(gradientBorder && {
              '--container-component-gradient-border-style': gradientBorder.borderStyle || 'dashed',
              ...(flex && { '--container-component-flex': flex }),
            }),
            ...(aspectRatio && { '--container-aspect-ratio': aspectRatio }),
            ...(transitionDuration ? { '--container-transition-duration': transitionDuration } : {}),
            ...(opacity || opacity === 0 ? { '--container-opacity': opacity } : {}),
            ...(boxShadow ? { '--container-box-shadow': boxShadow } : {}),
            ...style,
          } as CSSProperties
        }
        onClick={onClick}
        {...rest}
      >
        {children}
        {loading ? (
          <div className={styles.loadingContainer}>
            <WaitingIndicator />
          </div>
        ) : null}
      </Tag>
    );
  }
);

Container.displayName = 'Container';

export default Container;
